import { useState } from "react";
import classes from "./MediaLibraryItem.module.less";
import type { MediaItem } from "~/features/mediaLibrary/types/mediaLibraryTypes";
import Tag from "~/common/components/Tag/Tag";
import { IconButton, Stack, styled, Typography } from "~/common/mui";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import FavoriteOutlinedIcon from "@mui/icons-material/FavoriteOutlined";
import PlayCircleFilledWhiteOutlinedIcon from "@mui/icons-material/PlayCircleFilledWhiteOutlined";
import BrokenImageOutlinedIcon from "@mui/icons-material/BrokenImageOutlined";
import FolderZipOutlinedIcon from "@mui/icons-material/FolderZipOutlined";
import { MEDIA_TYPES, MediaType } from "~/features/mediaLibrary/constants";
import { lineClamp } from "~/common/components/icon/FontUtils";
import { C_ON_SURFACE, C_ON_SURFACE_VARIANT } from "~/common/components/icon/Colors";

export default function MediaLibraryItem(props: {
  media: MediaItem;
  isDownloading: boolean;
  handleCardClick: (media: MediaItem) => void;
  handleDownload: (media: MediaItem) => void;
  handleFavouriteClick: (
    id: string,
    updatedFav: boolean,
    title?: string,
    language?: string,
    mediaType?: string,
  ) => void;
}) {
  const { media, handleCardClick, handleFavouriteClick, handleDownload, isDownloading } = props;
  const { id, previewUrl, mediaType, isFavourite, title, language, documentType } = media;

  const handleFavourite = async () => {
    await handleFavouriteClick(id, !isFavourite, title, language, mediaType);
  };

  const mediaActionOverLay = () => (
    <div className={classes.cardHeader}>
      {language && <Tag className={classes.langTag}>{language}</Tag>}
      <div className={classes.actions}>
        <IconButton
          variant="contained"
          size={"medium"}
          color={"secondary"}
          disabled={isDownloading}
          onClick={async (e) => {
            e.stopPropagation();
            e.preventDefault();
            handleDownload(media);
          }}
        >
          <FileDownloadOutlinedIcon />
        </IconButton>
        <IconButton
          variant="contained"
          size={"medium"}
          color={"secondary"}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            handleFavourite();
          }}
        >
          {isFavourite ? <FavoriteOutlinedIcon /> : <FavoriteBorderOutlinedIcon />}
        </IconButton>
      </div>
    </div>
  );

  const mediaPlayIcon = () => {
    return (
      MEDIA_TYPES[mediaType] == MediaType.VIDEO && (
        <IconButton
          className={classes.playIcon}
          variant="contained"
          size={"medium"}
          color={"secondary"}
          onClick={() => {}}
        >
          <PlayCircleFilledWhiteOutlinedIcon />
        </IconButton>
      )
    );
  };

  return (
    <>
      <div
        className={classes.item}
        key={id}
        onClick={() => {
          handleCardClick(media);
        }}
      >
        <div className={classes.card}>
          {mediaActionOverLay()}
          {mediaPlayIcon()}
          <ImageWithFallback previewUrl={previewUrl} mediaType={mediaType} className={classes.image} />
        </div>
        <Stack gap={0.5}>
          <TruncatedTitle variant="titleMedium">{title}</TruncatedTitle>
          <TruncatedSubTitle>{documentType}</TruncatedSubTitle>
        </Stack>
      </div>
    </>
  );
}

export const ImageWithFallback = ({
  previewUrl,
  mediaType,
  className,
}: {
  previewUrl: string;
  mediaType: string;
  className: string;
}) => {
  const [loading, setLoading] = useState(true);
  const [imgError, setImgError] = useState(false);

  if (!previewUrl) {
    return fallbackImage();
  }

  if (MEDIA_TYPES[mediaType] === MediaType.ZIP) {
    return (
      <div className={classes.playIcon}>
        <FolderZipOutlinedIcon className={classes.unknownFile} />
      </div>
    );
  }

  return (
    <>
      {imgError && !loading ? (
        fallbackImage()
      ) : (
        <img
          src={previewUrl}
          alt={mediaType}
          className={className}
          onLoad={() => setLoading(false)}
          onError={() => {
            setLoading(false);
            setImgError(true);
          }}
        />
      )}
    </>
  );
};

const fallbackImage = () => {
  return (
    <div className={classes.playIcon}>
      <BrokenImageOutlinedIcon className={classes.unknownFile} />
    </div>
  );
};

const TruncatedTitle = styled(Typography)({
  ...lineClamp(2),
  color: C_ON_SURFACE,
});
const TruncatedSubTitle = styled(Typography)({
  ...lineClamp(1),
  color: C_ON_SURFACE_VARIANT,
});
